import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../ThemeContext';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
  DragOverlay
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  TrendingUp, TrendingDown, DollarSign, Users, Package, Activity,
  CheckCircle, Clock, AlertCircle, Calendar, ArrowUpRight, Eye,
  MessageSquare, Share2, MoreVertical, BarChart3,
  PieChart, Target, Zap, Star, Award, Briefcase, ClipboardCheck,
  Archive, Wrench, ScanLine, Monitor, BookOpen, BarChart2,
  ShoppingCart, Box, Palette, Building2, GraduationCap,
  Sun, Cloud, CloudRain, CloudSnow, CloudLightning, CloudDrizzle, Thermometer, Bell, X, Plus,
  Image, Video, Settings, GripVertical, StickyNote, MapPin,
  FileText, Timer, AlertTriangle, CheckCircle2, ChevronLeft, ChevronRight,
  Heart, MessageCircle, LayoutDashboard, ClipboardList, Receipt, Bug, FileCheck, Boxes,
  Maximize2, Minimize2, Warehouse, Truck, Home
} from 'lucide-react';
import { AreaChart, Area, BarChart as RechartsBarChart, Bar, PieChart as RechartsPieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, RadialBarChart, RadialBar } from 'recharts';

// Subtle, modern animation (less bulky movement)
const cardVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.04, duration: 0.35, ease: [0.4, 0, 0.2, 1] }
  }),
  hover: { y: -2, scale: 1.01, transition: { duration: 0.2 } }
};

// Clean, compact stat card - No charts, minimal design
const GradientStatCard = React.memo(({ title, value, change, trend, gradient, icon: Icon, subtitle, delay = 0, onRemove, isEditable, index }) => {
  const { isDarkMode } = useTheme();
  const accentMap = {
    'linear-gradient(135deg, #f97316 0%, #ec4899 100%)': { gradient: 'from-amber-500 to-rose-500', color: '#f97316' },
    'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)': { gradient: 'from-blue-500 to-cyan-500', color: '#3b82f6' },
    'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)': { gradient: 'from-violet-500 to-pink-500', color: '#a855f7' },
    'linear-gradient(135deg, #10b981 0%, #059669 100%)': { gradient: 'from-emerald-500 to-green-600', color: '#10b981' },
    'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)': { gradient: 'from-red-500 to-red-600', color: '#ef4444' },
    'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)': { gradient: 'from-amber-500 to-amber-600', color: '#f59e0b' },
  };
  const accent = accentMap[gradient] || { gradient: 'from-indigo-500 to-violet-500', color: '#6366f1' };
  
  return (
    <div className={`relative group cursor-pointer transition-all duration-200 ease-out rounded-xl p-3.5 shadow-md overflow-hidden h-full flex flex-col hover:scale-[1.02] ${
      isDarkMode 
        ? 'bg-white/10 backdrop-blur-md border border-white/20 hover:border-white/35 hover:bg-white/15' 
        : 'bg-gradient-to-br from-white to-slate-50 border border-slate-200/60 hover:border-cyan-400/60 hover:shadow-md hover:shadow-cyan-100/30'
    }`}>
      {isEditable && onRemove && (
        <button onClick={(e) => { e.stopPropagation(); onRemove(); }} className={`absolute top-1.5 right-1.5 p-1 opacity-0 group-hover:opacity-100 rounded-md z-10 transition-opacity ${
          isDarkMode ? 'hover:bg-white/20' : 'hover:bg-cyan-50'
        }`}>
          <X className={`h-3 w-3 ${isDarkMode ? 'text-white' : 'text-slate-500'}`} />
        </button>
      )}
      <div className="flex flex-col gap-2 flex-1 min-h-0 relative z-10">
        <div className={`p-2 rounded-lg bg-gradient-to-br ${accent.gradient} shadow-md flex-shrink-0 w-fit`}>
          <Icon className="h-3.5 w-3.5 text-white" />
        </div>
        <div className="flex-1 min-w-0 flex flex-col">
          <p className={`text-[10px] font-medium uppercase tracking-wider truncate mb-1 ${
            isDarkMode ? 'text-white/60' : 'text-slate-500'
          }`}>{title}</p>
          <div className="flex items-baseline gap-1.5 flex-1">
            <span className={`text-base font-bold tabular-nums leading-tight ${
              isDarkMode ? 'text-white' : 'text-slate-900'
            }`}>{value}</span>
            {change && (
              <span className={`flex items-center gap-0.5 text-[10px] font-semibold ${
                trend === 'up' 
                  ? (isDarkMode ? 'text-emerald-400' : 'text-emerald-700')
                  : trend === 'down' 
                    ? (isDarkMode ? 'text-red-400' : 'text-red-700')
                    : (isDarkMode ? 'text-white/60' : 'text-slate-600')
              }`}>
                {trend === 'up' ? <TrendingUp className="h-2.5 w-2.5" /> : trend === 'down' ? <TrendingDown className="h-2.5 w-2.5" /> : null}
                {change}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

// White Card Component - Theme-aware
const WhiteCard = ({ title, value, subtitle, icon: Icon, gradient, children, link, delay = 0, onRemove, isEditable, index }) => {
  const { isDarkMode } = useTheme();
  return (
  <motion.div
    custom={index}
    initial="hidden"
    animate="visible"
    whileHover="hover"
    variants={cardVariants}
    className={`relative group cursor-pointer transition-all duration-500 ease-out hover:scale-105 rounded-3xl p-6 shadow-lg overflow-hidden ${
      isDarkMode 
        ? 'bg-white/10 backdrop-blur-xl border border-white/20' 
        : 'bg-white shadow-zinc-200/40 border border-zinc-100 hover:shadow-2xl hover:border-zinc-200'
    }`}
  >
    {isDarkMode && (
      <div className={`absolute -inset-2 bg-gradient-to-br ${gradient} opacity-20 blur-2xl group-hover:opacity-40 transition-opacity`} />
    )}
    {isEditable && onRemove && (
      <button
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        className={`absolute top-3 right-3 p-1.5 opacity-0 group-hover:opacity-100 rounded-lg transition-all z-10 ${
          isDarkMode ? 'hover:bg-white/20' : 'hover:bg-zinc-100'
        }`}
      >
        <X className={`h-4 w-4 ${isDarkMode ? 'text-white' : 'text-zinc-500'}`} />
      </button>
    )}
    
    <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-300`} />
    
    <div className="relative z-10">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`p-3 rounded-xl bg-gradient-to-br ${gradient} shadow-lg`}>
            <Icon className="h-6 w-6 text-white" />
          </div>
          <div>
            <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>{title}</h3>
            {subtitle && <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-white/60' : 'text-zinc-600'}`}>{subtitle}</p>}
            {value && <p className={`text-2xl md:text-3xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>{value}</p>}
          </div>
        </div>
        {link && !isEditable && (
          <Link to={link} className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-white/20' : 'hover:bg-zinc-50'}`}>
            <ArrowUpRight className={`h-5 w-5 ${isDarkMode ? 'text-white/60' : 'text-zinc-400'}`} />
          </Link>
        )}
      </div>
      
      {children}
    </div>
  </motion.div>
  );
};

// Compact Weather Widget for Header
const CompactWeatherWidget = () => {
  const { isDarkMode } = useTheme();
  const weather = {
    city: 'Riyadh',
    temp: 28,
    condition: 'Sunny',
    icon: Sun,
  };
  
  return (
    <div className={`relative group flex items-center gap-3 px-4 py-2 rounded-xl shadow-lg overflow-hidden ${
      isDarkMode 
        ? 'bg-white/10 backdrop-blur-xl border border-white/20 text-white' 
        : 'bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-500 text-white'
    }`}>
      {isDarkMode && (
        <div className="absolute -inset-2 bg-gradient-to-br from-blue-500 to-indigo-500 opacity-20 blur-2xl group-hover:opacity-40 transition-opacity" />
      )}
      <div className="relative z-10 flex items-center gap-3">
        <weather.icon className="h-5 w-5 text-white" />
        <div className="flex items-baseline gap-1">
          <span className="text-lg font-bold tabular-nums">{weather.temp}</span>
          <span className="text-xs opacity-90">°C</span>
        </div>
        <div className="hidden sm:block">
          <p className="text-xs font-medium">{weather.condition}</p>
          <p className="text-[10px] opacity-80">{weather.city}</p>
        </div>
      </div>
    </div>
  );
};

// Weather Widget – Complete redesign with temperature chart
const WeatherWidget = ({ onRemove, isEditable, index }) => {
  const [weather] = useState({ temp: 28, city: 'Riyadh', condition: 'Sunny', icon: Sun });
  const tempData = [
    { time: '6AM', temp: 22 },
    { time: '9AM', temp: 25 },
    { time: '12PM', temp: 28 },
    { time: '3PM', temp: 30 },
    { time: '6PM', temp: 27 },
    { time: '9PM', temp: 24 },
  ];
  
  return (
    <div 
      className="relative group bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-500 rounded-2xl p-6 text-white overflow-hidden border-2 border-blue-400/30 shadow-xl"
    >
      {isEditable && onRemove && (
        <button onClick={(e) => { e.stopPropagation(); onRemove(); }} className="absolute top-4 right-4 p-1.5 opacity-0 group-hover:opacity-100 hover:bg-white/20 rounded-lg z-10 transition-opacity">
          <X className="h-4 w-4 text-white" />
        </button>
      )}
      
      {/* Background */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(255,255,255,0.2),transparent_60%)]" />
      
      <div className="relative z-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <p className="text-xs font-bold text-white/90 uppercase tracking-wider mb-1.5">{weather.city}</p>
            <div className="flex items-baseline gap-1">
              <span className="text-4xl font-bold tabular-nums">{weather.temp}</span>
              <span className="text-xl opacity-90">°C</span>
            </div>
            <p className="text-sm text-white/90 mt-1.5 font-semibold">{weather.condition}</p>
          </div>
          <div className="relative">
            <div className="absolute inset-0 bg-white/20 rounded-full blur-xl" />
            <weather.icon className="h-14 w-14 text-white relative" />
          </div>
        </div>
        
        {/* Temperature Chart */}
        <div className="h-20 w-full bg-white/15 backdrop-blur-sm rounded-lg p-3 border border-white/20">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={tempData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
              <defs>
                <linearGradient id="tempGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="rgba(255,255,255,0.5)" />
                  <stop offset="100%" stopColor="rgba(255,255,255,0)" />
                </linearGradient>
              </defs>
              <Area type="monotone" dataKey="temp" stroke="rgba(255,255,255,0.9)" strokeWidth={2.5} fill="url(#tempGradient)" />
              <XAxis dataKey="time" tick={{ fill: 'rgba(255,255,255,0.7)', fontSize: 10 }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

// Tasks Widget – Ultra compact with modal
const TasksWidget = ({ onRemove, isEditable, index, size = 'medium' }) => {
  const { isDarkMode } = useTheme();
  const [showModal, setShowModal] = useState(false);
  const tasks = [
    { id: 1, title: 'Review Q2 Budget Report', priority: 'high', dueDate: 'Today', description: 'Financial review and approval needed', assignee: 'John Doe' },
    { id: 2, title: 'Approve Maintenance Request #45', priority: 'medium', dueDate: 'Tomorrow', description: 'AC unit repair in Office 3B', assignee: 'Sarah Smith' },
    { id: 3, title: 'Update Inventory Records', priority: 'low', dueDate: 'In 2 days', description: 'Quarterly inventory audit', assignee: 'Mike Johnson' },
    { id: 4, title: 'Client Presentation Prep', priority: 'high', dueDate: 'Today', description: 'Prepare slides for Q2 review', assignee: 'You' },
  ];
  
  const isSmall = size === 'small';
  const isLarge = size === 'large';
  const highPriorityCount = tasks.filter(t => t.priority === 'high').length;
  
  return (
    <>
      <div 
        className={`relative group rounded-lg border transition-all h-full flex flex-col ${isSmall ? 'p-2' : isLarge ? 'p-4' : 'p-3'} ${
          isDarkMode ? 'bg-white/10 backdrop-blur-xl border-white/20 hover:border-white/30' : 'bg-white border-slate-200 hover:border-emerald-300 hover:shadow-md'
        }`}
      >
        {isEditable && onRemove && (
          <button onClick={(e) => { e.stopPropagation(); onRemove(); }} className={`absolute top-1 right-1 p-0.5 opacity-0 group-hover:opacity-100 rounded z-10 transition-opacity ${isDarkMode ? 'hover:bg-white/20' : 'hover:bg-emerald-50'}`}>
            <X className={`h-2.5 w-2.5 ${isDarkMode ? 'text-white/70' : 'text-slate-500'}`} />
          </button>
        )}
        
        {/* Small: Icon + Number only */}
        {isSmall ? (
          <div className="flex flex-col items-center justify-center h-full gap-1">
            <div className="p-2 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600">
              <Clock className="h-4 w-4 text-white" />
            </div>
            <div className="text-center">
              <div className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{tasks.length}</div>
              {highPriorityCount > 0 && (
                <div className="text-xs font-semibold text-red-500">{highPriorityCount} urgent</div>
              )}
            </div>
            <button 
              onClick={() => setShowModal(true)}
              className={`mt-1 text-[10px] font-medium ${isDarkMode ? 'text-emerald-400 hover:text-emerald-300' : 'text-emerald-600 hover:text-emerald-700'}`}
            >
              View
            </button>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <div className="p-1 rounded-md bg-gradient-to-br from-emerald-500 to-teal-600">
                  <Clock className="h-3 w-3 text-white" />
                </div>
                <h3 className={`text-xs font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Tasks</h3>
                <span className={`text-[10px] font-semibold text-emerald-500 px-1 py-0.5 rounded ${isDarkMode ? 'bg-white/10' : 'bg-emerald-50'}`}>{tasks.length}</span>
              </div>
              <button 
                onClick={() => setShowModal(true)}
                className={`p-0.5 rounded transition-colors ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`}
                title="View all tasks"
              >
                <Eye className={`h-3 w-3 ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`} />
              </button>
            </div>

            {/* Tasks List - Compact */}
            <div className="flex-1 space-y-1">
              {(isLarge ? tasks : tasks.slice(0, 3)).map((task) => (
                <div
                  key={task.id}
                  className={`flex items-center gap-1.5 p-1.5 rounded transition-colors cursor-pointer ${isDarkMode ? 'bg-white/5 hover:bg-white/10' : 'bg-slate-50 hover:bg-emerald-50/50'}`}
                >
                  <div className={`w-1 h-1 rounded-full flex-shrink-0 ${
                    task.priority === 'high' ? 'bg-red-500' :
                    task.priority === 'medium' ? 'bg-amber-500' :
                    'bg-slate-400'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-[10px] font-semibold truncate leading-tight ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{task.title}</p>
                    <p className={`text-[9px] ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>{task.dueDate}</p>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className={`rounded-xl max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-2xl ${isDarkMode ? 'bg-[#1a1a1a] border border-white/20' : 'bg-white'}`}
            >
              <div className={`p-4 border-b flex items-center justify-between ${isDarkMode ? 'border-white/20' : 'border-slate-200'}`}>
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600">
                    <Clock className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>My Tasks</h3>
                    <p className={`text-xs ${isDarkMode ? 'text-white/60' : 'text-slate-600'}`}>{tasks.length} active tasks</p>
                  </div>
                </div>
                <button onClick={() => setShowModal(false)} className={`p-1 rounded ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`}>
                  <X className={`h-5 w-5 ${isDarkMode ? 'text-white/70' : 'text-slate-500'}`} />
                </button>
              </div>
              <div className="p-4 overflow-y-auto max-h-[60vh] space-y-2">
                {tasks.map((task) => (
                  <div key={task.id} className={`p-3 rounded-lg border ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
                    <div className="flex items-start gap-3">
                      <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                        task.priority === 'high' ? 'bg-red-500' :
                        task.priority === 'medium' ? 'bg-amber-500' :
                        'bg-slate-400'
                      }`} />
                      <div className="flex-1">
                        <p className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{task.title}</p>
                        <p className={`text-xs mt-1 ${isDarkMode ? 'text-white/60' : 'text-slate-600'}`}>{task.description}</p>
                        <div className={`flex items-center gap-3 mt-2 text-xs ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>
                          <span>{task.dueDate}</span>
                          <span>•</span>
                          <span>{task.assignee}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Stacked Carousel Company Announcements Widget
const AnnouncementsWidget = ({ onRemove, isEditable, index }) => {
  const { isDarkMode } = useTheme();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoRotating, setIsAutoRotating] = useState(true);
  const [progress, setProgress] = useState(0);
  const ROTATION_DURATION = 8000; // 8 seconds

  const announcements = [
    { 
      id: 1, 
      title: 'Q2 Company Meeting Scheduled', 
      description: 'Join us for the quarterly company meeting on Friday at 2 PM. We will discuss Q2 results, upcoming projects, and company goals.',
      date: '2 days ago', 
      type: 'meeting',
      author: 'HR Department',
      priority: 'high',
      icon: Calendar,
      color: 'from-blue-500 to-cyan-500',
      image: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=800'
    },
    { 
      id: 2, 
      title: 'New HR Policies Effective', 
      description: 'Updated employee handbook and new policies are now in effect. Please review the updated policies in your employee portal.',
      date: '1 week ago', 
      type: 'policy',
      author: 'HR Department',
      priority: 'high',
      icon: FileText,
      color: 'from-purple-500 to-pink-500',
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800'
    },
    { 
      id: 3, 
      title: 'System Maintenance This Weekend', 
      description: 'Scheduled maintenance from 2 AM to 4 AM. System will be unavailable during this time. Please save your work.',
      date: '3 days ago', 
      type: 'system',
      author: 'IT Department',
      priority: 'medium',
      icon: Monitor,
      color: 'from-orange-500 to-red-500',
      image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800'
    },
    { 
      id: 4, 
      title: 'New Employee Onboarding Program', 
      description: 'Launching comprehensive onboarding program for all new hires. The program includes orientation sessions and training modules.',
      date: '5 days ago', 
      type: 'announcement',
      author: 'HR Department',
      priority: 'low',
      icon: Users,
      color: 'from-green-500 to-emerald-500',
      image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800'
    },
    { 
      id: 5, 
      title: 'Annual Company Retreat', 
      description: 'Save the date! Our annual company retreat is scheduled for next month. More details and registration coming soon.',
      date: '1 week ago', 
      type: 'event',
      author: 'Events Team',
      priority: 'medium',
      icon: Calendar,
      color: 'from-indigo-500 to-purple-500',
      image: 'https://images.unsplash.com/photo-1511578314322-379afb476865?w=800'
    },
  ];

  // Auto-rotation with progress bar
  useEffect(() => {
    if (!isAutoRotating) {
      setProgress(0);
      return;
    }
    
    // Reset progress when index changes
    setProgress(0);
    
    // Progress bar animation - smooth updates
    const stepMs = 100;
    const stepCount = ROTATION_DURATION / stepMs;
    const stepValue = 100 / stepCount;
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) return 0;
        return Math.min(100, prev + stepValue);
      });
    }, stepMs);

    // Auto-rotation
    const rotationInterval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % announcements.length);
      setProgress(0);
    }, ROTATION_DURATION);

    return () => {
      clearInterval(progressInterval);
      clearInterval(rotationInterval);
    };
  }, [isAutoRotating, announcements.length, currentIndex]);

  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'high': return 'from-red-500 to-rose-500';
      case 'medium': return 'from-orange-500 to-amber-500';
      default: return 'from-blue-500 to-indigo-500';
    }
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % announcements.length);
    setProgress(0);
    setIsAutoRotating(false);
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + announcements.length) % announcements.length);
    setProgress(0);
    setIsAutoRotating(false);
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
    setProgress(0);
    setIsAutoRotating(false);
  };

  return (
    <div
      className={`relative group rounded-3xl p-6 border transition-shadow overflow-hidden [contain:paint] ${
        isDarkMode ? 'bg-white/10 border-white/20 hover:border-white/30' : 'bg-white border-cyan-100 hover:border-cyan-200 hover:shadow-2xl'
      }`}
      onMouseEnter={() => setIsAutoRotating(false)}
      onMouseLeave={() => setIsAutoRotating(true)}
      style={isDarkMode ? { backdropFilter: 'none' } : undefined}
    >
      {isEditable && onRemove && (
        <button onClick={(e) => { e.stopPropagation(); onRemove(); }} className={`absolute top-4 right-4 p-2 opacity-0 group-hover:opacity-100 rounded-lg z-20 transition-opacity ${isDarkMode ? 'hover:bg-white/20' : 'hover:bg-cyan-50'}`}>
          <X className={`h-4 w-4 ${isDarkMode ? 'text-white/70' : 'text-slate-500'}`} />
        </button>
      )}
      <div className="flex items-center justify-between mb-5 relative z-10">
        <div className="flex items-center gap-4">
          <div className="p-4 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl shadow-lg">
            <Bell className="h-6 w-6 text-white" />
          </div>
          <div>
            <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent'}`}>
              Company Updates
            </h3>
            <p className={`text-sm mt-1 ${isDarkMode ? 'text-white/60' : 'text-slate-600'}`}>{announcements.length} announcements</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {announcements.map((_, idx) => (
            <button
              key={idx}
              onClick={() => goToSlide(idx)}
              className={`h-2 rounded-full transition-all ${
                idx === currentIndex 
                  ? 'w-8 bg-gradient-to-r from-cyan-500 to-blue-500' 
                  : isDarkMode ? 'w-2 bg-white/30 hover:bg-white/50' : 'w-2 bg-cyan-200 hover:bg-cyan-300'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Stacked Cards Container - overflow-hidden so slides don't overlap left column on transition */}
      <div className="relative h-[280px] sm:h-[320px] perspective-1000 overflow-hidden">
        {announcements.map((announcement, idx) => {
          const Icon = announcement.icon;
          const isActive = idx === currentIndex;
          const offset = idx - currentIndex;
          const absOffset = Math.abs(offset);
          const isVisible = absOffset <= 2; // Show current + 2 behind

          if (!isVisible) return null;

          return (
            <div
              key={announcement.id}
              className="absolute inset-0 cursor-pointer transition-all duration-300"
              style={{
                zIndex: announcements.length - absOffset,
                transform: `scale(${1 - absOffset * 0.03})`,
                opacity: isActive ? 1 : 0.5 - absOffset * 0.1,
              }}
              onClick={() => goToSlide(idx)}
            >
              <div className={`relative h-full rounded-3xl shadow-2xl border overflow-hidden ${
                isDarkMode ? 'bg-[#222] border-white/25' : 'bg-white border-cyan-100'
              }`}>
                {/* Gradient Background Accent */}
                <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${announcement.color} opacity-10 blur-3xl`} />
                
                <div className="relative h-full flex">
                  {/* Image Side - Left */}
                  {announcement.image && (
                    <div className="relative w-2/5 overflow-hidden">
                      <img 
                        src={announcement.image} 
                        alt={announcement.title}
                        className="w-full h-full object-cover"
                      />
                      
                      {/* Gradient Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-black/10 to-transparent" />
                      
                      {/* Priority Badge */}
                      <div className="absolute top-4 right-4">
                        <span className={`px-3 py-1.5 rounded-full text-xs font-bold bg-gradient-to-r ${getPriorityColor(announcement.priority)} text-white shadow-xl backdrop-blur-sm`}>
                          {announcement.priority.toUpperCase()}
                        </span>
                      </div>

                      {/* Icon Badge */}
                      <div className={`absolute bottom-4 left-4 p-3 rounded-xl bg-gradient-to-br ${announcement.color} shadow-xl backdrop-blur-sm`}>
                        <Icon className="h-5 w-5 text-white" />
                      </div>

                      {/* Pattern Overlay */}
                      <div className="absolute inset-0 opacity-5" style={{
                        backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
                        backgroundSize: '20px 20px'
                      }} />
                    </div>
                  )}

                  {/* Content Side - Right - a bit lighter than the slide card for contrast */}
                  <div className={`flex-1 flex flex-col p-6 ${isDarkMode ? 'bg-[#2a2a2a]' : 'bg-gradient-to-br from-white to-cyan-50/30'}`}>
                    {/* Header */}
                    <div className="mb-4">
                      <h4 className={`text-xl font-bold mb-3 leading-tight transition-opacity ${isActive ? 'opacity-100' : 'opacity-70'} ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {announcement.title}
                      </h4>
                      
                      {/* Type Badge */}
                      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${isDarkMode ? 'bg-white/10 border-white/20' : 'bg-cyan-100 border-cyan-200'}`}>
                        <Icon className={`h-4 w-4 ${announcement.color.replace('from-', 'text-').replace('to-', '')}`} />
                        <span className={`text-xs font-semibold capitalize ${isDarkMode ? 'text-white/80' : 'text-slate-700'}`}>{announcement.type}</span>
                      </div>
                    </div>

                    {/* Description */}
                    <motion.p 
                      className={`text-sm mb-5 leading-relaxed flex-1 line-clamp-3 ${isDarkMode ? 'text-white/70' : 'text-slate-600'}`}
                      initial={false}
                      animate={isActive ? { opacity: 1 } : { opacity: 0.6 }}
                    >
                      {announcement.description}
                    </motion.p>

                    {/* Footer */}
                    <div className={`pt-4 border-t ${isDarkMode ? 'border-white/20' : 'border-cyan-100'}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="relative w-10 h-10 rounded-full bg-gradient-to-r from-cyan-400 to-blue-400 flex items-center justify-center text-white text-sm font-bold shadow-lg">
                            <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-blue-400 rounded-full blur-md opacity-50" />
                            <span className="relative z-10">{announcement.author[0]}</span>
                          </div>
                          <div>
                            <p className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{announcement.author}</p>
                            <p className={`text-xs ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>{announcement.date}</p>
                          </div>
                        </div>
                        
                        {/* Read More Indicator */}
                        {isActive && (
                          <div className={`flex items-center gap-1 text-xs font-medium ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>
                            <span>Read more</span>
                            <ArrowUpRight className="h-3.5 w-3.5" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Active Indicator - Animated Progress Bar */}
                {isActive && (
                  <div className={`absolute bottom-0 left-0 right-0 h-1 overflow-hidden rounded-b-2xl ${isDarkMode ? 'bg-white/15' : 'bg-gray-200'}`}>
                    <motion.div
                      className={`h-full rounded-full bg-gradient-to-r ${announcement.color}`}
                      initial={{ width: '0%' }}
                      animate={{ width: isAutoRotating ? `${progress}%` : '100%' }}
                      transition={isAutoRotating ? { duration: 0.1, ease: 'linear' } : { duration: 0.3, ease: 'easeOut' }}
                    />
                  </div>
                )}

              </div>
            </div>
          );
        })}
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          goToPrevious();
        }}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm shadow-lg flex items-center justify-center text-gray-700 hover:bg-white hover:scale-110 transition-all opacity-0 group-hover:opacity-100"
      >
        <ChevronLeft className="h-5 w-5" />
      </button>
       <button
         onClick={(e) => {
           e.stopPropagation();
           goToNext();
         }}
         className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm shadow-lg flex items-center justify-center text-gray-700 hover:bg-white hover:scale-110 transition-all opacity-0 group-hover:opacity-100"
       >
         <ChevronRight className="h-5 w-5" />
       </button>
    </div>
  );
};

// Story Viewer Component
const StoryViewer = ({ story, isOpen, onClose }) => {
  const mockStoryContent = [
    { type: 'image', url: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=800', duration: 5000 },
    { type: 'text', text: 'Working on exciting new projects! 🚀', duration: 3000 },
    { type: 'image', url: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800', duration: 5000 },
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(42);
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [comments, setComments] = useState([
    { id: 1, author: 'Sarah Johnson', text: 'Great work! 🎉', time: '2m ago' },
    { id: 2, author: 'Mike Davis', text: 'Looking forward to this!', time: '5m ago' },
  ]);

  useEffect(() => {
    if (!isOpen || !story) return;
    
    // Reset when story changes
    setCurrentIndex(0);
    setProgress(0);
    
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          if (currentIndex < mockStoryContent.length - 1) {
            setCurrentIndex(prev => prev + 1);
            return 0;
          } else {
            onClose();
            return 0;
          }
        }
        return prev + 2;
      });
    }, mockStoryContent[currentIndex]?.duration / 50 || 100);

    return () => clearInterval(interval);
  }, [isOpen, currentIndex, story, onClose]);

  if (!isOpen || !story) return null;

  const currentStory = mockStoryContent[currentIndex];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black flex items-center justify-center"
      onClick={onClose}
    >
      <div className="relative w-full h-full max-w-md mx-auto">
        {/* Progress Bars */}
        <div className="absolute top-4 left-4 right-4 flex gap-1 z-10">
          {mockStoryContent.map((_, idx) => (
            <div key={idx} className="flex-1 h-1 bg-white/30 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-white rounded-full"
                initial={{ width: idx < currentIndex ? '100%' : '0%' }}
                animate={{ width: idx === currentIndex ? `${progress}%` : idx < currentIndex ? '100%' : '0%' }}
                transition={{ duration: 0.1 }}
              />
            </div>
          ))}
        </div>

        {/* Story Content */}
        <div className="relative w-full h-full flex items-center justify-center">
          {currentStory?.type === 'image' ? (
            <img 
              src={currentStory.url} 
              alt="Story" 
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="bg-gradient-to-br from-purple-500 to-pink-500 w-full h-full flex items-center justify-center p-8">
              <p className="text-white text-2xl font-bold text-center">{currentStory?.text}</p>
            </div>
          )}
        </div>

        {/* User Info */}
        <div className="absolute top-16 left-4 flex items-center gap-3 z-10">
          <div className="w-10 h-10 rounded-full bg-gradient-to-r from-orange-500 to-pink-500 p-0.5">
            <div className="w-full h-full rounded-full bg-gray-800 flex items-center justify-center">
              <span className="text-white text-xs font-bold">{story.avatar}</span>
            </div>
          </div>
          <div>
            <p className="text-white font-semibold">{story.name}</p>
            <p className="text-white/70 text-xs">{story.role}</p>
          </div>
        </div>

        {/* Navigation */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (currentIndex > 0) {
              setCurrentIndex(prev => prev - 1);
              setProgress(0);
            }
          }}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/30 transition-colors"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (currentIndex < mockStoryContent.length - 1) {
              setCurrentIndex(prev => prev + 1);
              setProgress(0);
            } else {
              onClose();
            }
          }}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/30 transition-colors"
        >
          <ChevronRight className="h-6 w-6" />
        </button>

        {/* Like and Comment Buttons */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-4 z-10">
          <motion.button
            onClick={(e) => {
              e.stopPropagation();
              setLiked(!liked);
              setLikeCount(prev => liked ? prev - 1 : prev + 1);
            }}
            className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-colors"
            whileTap={{ scale: 0.9 }}
          >
            <Heart 
              className={`h-6 w-6 ${liked ? 'fill-red-500 text-red-500' : 'text-white'}`}
            />
            <span className="text-white font-semibold">{likeCount}</span>
          </motion.button>
          
          <motion.button
            onClick={(e) => {
              e.stopPropagation();
              setShowComments(!showComments);
            }}
            className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-colors"
            whileTap={{ scale: 0.9 }}
          >
            <MessageCircle className="h-6 w-6 text-white" />
            <span className="text-white font-semibold">{comments.length}</span>
          </motion.button>
        </div>

        {/* Comments Panel */}
        <AnimatePresence>
          {showComments && (
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              onClick={(e) => e.stopPropagation()}
              className="absolute right-0 top-0 bottom-0 w-80 bg-black/95 backdrop-blur-xl border-l border-white/10 z-20 flex flex-col"
            >
              {/* Comments Header */}
              <div className="p-4 border-b border-white/10">
                <div className="flex items-center justify-between">
                  <h3 className="text-white font-bold text-lg">Comments</h3>
                  <button
                    onClick={() => setShowComments(false)}
                    className="p-2 hover:bg-white/10 rounded-full transition-colors"
                  >
                    <X className="h-5 w-5 text-white" />
                  </button>
                </div>
              </div>

              {/* Comments List */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {comments.map((comment) => (
                  <motion.div
                    key={comment.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex gap-3"
                  >
                    <div className="w-8 h-8 rounded-full bg-gradient-to-r from-orange-400 to-pink-400 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                      {comment.author[0]}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-white font-semibold text-sm">{comment.author}</span>
                        <span className="text-white/50 text-xs">{comment.time}</span>
                      </div>
                      <p className="text-white/90 text-sm">{comment.text}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Comment Input */}
              <div className="p-4 border-t border-white/10">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && commentText.trim()) {
                        setComments([...comments, {
                          id: comments.length + 1,
                          author: 'You',
                          text: commentText,
                          time: 'now'
                        }]);
                        setCommentText('');
                      }
                    }}
                    placeholder="Add a comment..."
                    className="flex-1 px-4 py-2 bg-white/10 border border-white/20 rounded-full text-white placeholder-white/50 focus:outline-none focus:border-white/40"
                  />
                  <motion.button
                    onClick={() => {
                      if (commentText.trim()) {
                        setComments([...comments, {
                          id: comments.length + 1,
                          author: 'You',
                          text: commentText,
                          time: 'now'
                        }]);
                        setCommentText('');
                      }
                    }}
                    whileTap={{ scale: 0.95 }}
                    className="p-2 rounded-full bg-gradient-to-r from-orange-500 to-pink-500 text-white"
                  >
                    <ArrowUpRight className="h-4 w-4" />
                  </motion.button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

// Instagram-like Team Stories Widget - Top Middle Placement
const StoriesWidget = ({ onRemove, isEditable, index }) => {
  const { isDarkMode } = useTheme();
  const [selectedStory, setSelectedStory] = useState(null);
  const [isStoryViewerOpen, setIsStoryViewerOpen] = useState(false);

  const stories = [
    { id: 1, name: 'John', avatar: 'JS', role: 'CEO', hasStory: true, image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200' },
    { id: 2, name: 'Sarah', avatar: 'SJ', role: 'CTO', hasStory: true, image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200' },
    { id: 3, name: 'Mike', avatar: 'MD', role: 'HR', hasStory: true, image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200' },
    { id: 4, name: 'Emma', avatar: 'EW', role: 'Ops', hasStory: false, image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200' },
    { id: 5, name: 'David', avatar: 'DB', role: 'Finance', hasStory: true, image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200' },
    { id: 6, name: 'Lisa', avatar: 'LC', role: 'Marketing', hasStory: true, image: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=200' },
    { id: 7, name: 'Alex', avatar: 'AT', role: 'Design', hasStory: true, image: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=200' },
    { id: 8, name: 'Maria', avatar: 'MG', role: 'Sales', hasStory: false, image: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=200' },
  ];

  const handleStoryClick = (story) => {
    if (story.hasStory) {
      setSelectedStory(story);
      setIsStoryViewerOpen(true);
    }
  };

  return (
    <>
      <div className={`relative group rounded-2xl p-4 border-2 transition-all ${
        isDarkMode ? 'bg-white/10 backdrop-blur-xl border-white/20 hover:border-white/30' : 'bg-white border-cyan-100 hover:border-cyan-200 hover:shadow-xl'
      }`}>
        {isEditable && onRemove && (
          <button onClick={(e) => { e.stopPropagation(); onRemove(); }} className={`absolute top-2 right-2 p-1.5 opacity-0 group-hover:opacity-100 rounded-lg z-10 transition-opacity ${isDarkMode ? 'hover:bg-white/20' : 'hover:bg-cyan-50'}`}>
            <X className={`h-3.5 w-3.5 ${isDarkMode ? 'text-white/70' : 'text-slate-500'}`} />
          </button>
        )}
        <div className="flex gap-4 overflow-x-auto pb-1 scrollbar-thin scrollbar-thumb-cyan-300 scrollbar-track-transparent">
          {stories.map((story) => (
            <div
              key={story.id}
              onClick={() => handleStoryClick(story)}
              className="flex flex-col items-center gap-2 flex-shrink-0 cursor-pointer group/item"
            >
              <div className="relative p-0.5">
                {story.hasStory && (
                  <div
                    className="absolute inset-0 rounded-full"
                    style={{ background: 'linear-gradient(135deg, #fbbf24, #f472b6, #a78bfa)', padding: '2px' }}
                  >
                    <div className="w-full h-full rounded-full bg-gradient-to-br from-indigo-500 to-purple-500" />
                  </div>
                )}
                <div className={`relative w-16 h-16 rounded-full overflow-hidden ${story.hasStory ? '' : 'border-2 border-orange-200'} bg-orange-50 group-hover/item:scale-110 transition-transform shadow-lg`}>
                  <img 
                    src={story.image} 
                    alt={story.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="w-full h-full bg-gradient-to-br from-orange-400 via-pink-400 to-purple-400 flex items-center justify-center text-white font-bold text-lg hidden">
                    {story.avatar}
                  </div>
                </div>
              </div>
              <div className="text-center w-16">
                <p className={`text-xs font-semibold truncate ${isDarkMode ? 'text-white/90' : 'text-slate-700'}`}>{story.name}</p>
              </div>
            </div>
          ))}
          <div className="flex flex-col items-center gap-2 flex-shrink-0 cursor-pointer">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center border-2 border-dashed transition-colors ${
              isDarkMode ? 'bg-white/10 border-white/30 hover:bg-white/20 hover:border-white/40' : 'bg-gradient-to-br from-cyan-100 to-blue-100 hover:from-cyan-200 hover:to-blue-200 border-cyan-300'
            }`}>
              <Plus className={`h-7 w-7 ${isDarkMode ? 'text-white/70' : 'text-cyan-600'}`} />
            </div>
            <p className={`text-xs font-semibold ${isDarkMode ? 'text-white/60' : 'text-slate-600'}`}>Your Story</p>
          </div>
        </div>
      </div>

      {/* Story Viewer */}
      <AnimatePresence>
        {isStoryViewerOpen && selectedStory && (
          <StoryViewer 
            story={selectedStory} 
            isOpen={isStoryViewerOpen} 
            onClose={() => {
              setIsStoryViewerOpen(false);
              setSelectedStory(null);
            }} 
          />
        )}
      </AnimatePresence>
    </>
  );
};

// Sticky Notes Widget – Ultra compact with modal
const StickyNotesWidget = ({ onRemove, isEditable, index, size = 'medium' }) => {
  const { isDarkMode } = useTheme();
  const [showModal, setShowModal] = useState(false);
  const [notes, setNotes] = useState([
    { id: 1, content: 'Review Q2 budget report', color: 'yellow', createdAt: '2 hours ago' },
    { id: 2, content: 'Call client about project', color: 'pink', createdAt: 'Yesterday' },
    { id: 3, content: 'Team meeting at 3 PM', color: 'blue', createdAt: 'Today' },
    { id: 4, content: 'Follow up on invoice #1234', color: 'yellow', createdAt: '3 days ago' },
    { id: 5, content: 'Update project timeline', color: 'pink', createdAt: 'Last week' },
  ]);
  
  const colorStyles = {
    yellow: { bg: 'bg-yellow-50', border: 'border-yellow-300', text: 'text-yellow-900' },
    pink: { bg: 'bg-pink-50', border: 'border-pink-300', text: 'text-pink-900' },
    blue: { bg: 'bg-blue-50', border: 'border-blue-300', text: 'text-blue-900' },
  };
  
  const isSmall = size === 'small';
  const isLarge = size === 'large';
  
  return (
    <>
      <div 
        className={`relative group rounded-lg border transition-all h-full flex flex-col ${isSmall ? 'p-2' : isLarge ? 'p-4' : 'p-3'} ${
          isDarkMode ? 'bg-white/10 backdrop-blur-xl border-white/20 hover:border-white/30' : 'bg-white border-slate-200 hover:border-violet-300 hover:shadow-md'
        }`}
      >
        {isEditable && onRemove && (
          <button onClick={(e) => { e.stopPropagation(); onRemove(); }} className={`absolute top-1 right-1 p-0.5 opacity-0 group-hover:opacity-100 rounded z-10 transition-opacity ${isDarkMode ? 'hover:bg-white/20' : 'hover:bg-violet-50'}`}>
            <X className={`h-2.5 w-2.5 ${isDarkMode ? 'text-white/70' : 'text-slate-500'}`} />
          </button>
        )}
        
        {/* Small: Icon + Number only */}
        {isSmall ? (
          <div className="flex flex-col items-center justify-center h-full gap-1">
            <div className="p-2 rounded-lg bg-gradient-to-br from-violet-400 to-purple-500">
              <StickyNote className="h-4 w-4 text-white" />
            </div>
            <div className="text-center">
              <div className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{notes.length}</div>
              <div className={`text-[9px] ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>notes</div>
            </div>
            <button 
              onClick={() => setShowModal(true)}
              className="mt-1 text-[10px] text-violet-600 hover:text-violet-700 font-medium"
            >
              View
            </button>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <div className="p-1 rounded-md bg-gradient-to-br from-violet-400 to-purple-500">
                  <StickyNote className="h-3 w-3 text-white" />
                </div>
                <h3 className={`text-xs font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Notes</h3>
                <span className={`text-[10px] font-semibold text-violet-500 px-1 py-0.5 rounded ${isDarkMode ? 'bg-white/10' : 'bg-violet-50'}`}>{notes.length}</span>
              </div>
              <button 
                onClick={() => setShowModal(true)}
                className={`p-0.5 rounded transition-colors ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`}
                title="View all notes"
              >
                <Eye className={`h-3 w-3 ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`} />
              </button>
            </div>
            
            {/* Notes List - Compact */}
            <div className="flex-1 mb-2 space-y-1">
              {(isLarge ? notes : notes.slice(0, 2)).map((note) => {
                const style = colorStyles[note.color];
                return (
                  <div
                    key={note.id}
                    className={`${style.bg} ${style.border} border rounded-md cursor-pointer hover:shadow-sm transition-all ${style.text} p-1.5`}
                  >
                    <p className="text-[10px] font-medium leading-snug line-clamp-2">{note.content}</p>
                  </div>
                );
              })}
            </div>
            
            <button className={`w-full flex items-center justify-center gap-1 py-1 border border-dashed rounded-md text-[10px] font-medium transition-all ${
              isDarkMode ? 'border-white/30 text-white/70 hover:border-white/50 hover:bg-white/10' : 'border-violet-300 text-violet-600 hover:border-violet-500 hover:bg-violet-50'
            }`}>
              <Plus className="h-2.5 w-2.5" /> Add
            </button>
          </>
        )}
      </div>

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className={`rounded-xl max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-2xl ${isDarkMode ? 'bg-[#1a1a1a] border border-white/20' : 'bg-white'}`}
            >
              <div className={`p-4 border-b flex items-center justify-between ${isDarkMode ? 'border-white/20' : 'border-slate-200'}`}>
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-violet-400 to-purple-500">
                    <StickyNote className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Quick Notes</h3>
                    <p className={`text-xs ${isDarkMode ? 'text-white/60' : 'text-slate-600'}`}>{notes.length} notes</p>
                  </div>
                </div>
                <button onClick={() => setShowModal(false)} className={`p-1 rounded ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`}>
                  <X className={`h-5 w-5 ${isDarkMode ? 'text-white/70' : 'text-slate-500'}`} />
                </button>
              </div>
              <div className="p-4 overflow-y-auto max-h-[60vh] space-y-2">
                {notes.map((note) => {
                  const style = colorStyles[note.color];
                  return (
                    <div key={note.id} className={`${style.bg} ${style.border} border rounded-lg p-3 ${style.text}`}>
                      <p className="text-sm font-medium">{note.content}</p>
                      <p className="text-xs text-slate-400 mt-1">{note.createdAt}</p>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Calendar Widget – Ultra compact with modal
const CalendarWidget = ({ onRemove, isEditable, index, size = 'medium' }) => {
  const { isDarkMode } = useTheme();
  const [showModal, setShowModal] = useState(false);
  const today = new Date();
  const currentDate = today.getDate();
  const currentMonth = today.toLocaleDateString('en-US', { month: 'short' });
  const currentMonthFull = today.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const events = [
    { date: 15, title: 'Team Meeting', time: '2:00 PM', color: 'blue', location: 'Conference Room A', attendees: 5 },
    { date: 18, title: 'Project Review', time: '10:00 AM', color: 'purple', location: 'Main Office', attendees: 8 },
    { date: 22, title: 'Client Call', time: '3:30 PM', color: 'orange', location: 'Online', attendees: 3 },
    { date: 25, title: 'Budget Planning', time: '11:00 AM', color: 'blue', location: 'Board Room', attendees: 6 },
  ];
  
  const isSmall = size === 'small';
  const isLarge = size === 'large';
  
  // Generate calendar days for large view
  const daysOfWeek = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  const calendarDays = Array.from({ length: 28 }, (_, i) => {
    const day = i + 1;
    const hasEvent = events.some(e => e.date === day);
    return { day, hasEvent, isToday: day === currentDate };
  });

  return (
    <>
      <div 
        className={`relative group rounded-lg border transition-all h-full flex flex-col ${isSmall ? 'p-2' : isLarge ? 'p-4' : 'p-3'} ${
          isDarkMode ? 'bg-white/10 backdrop-blur-xl border-white/20 hover:border-white/30' : 'bg-white border-slate-200 hover:border-indigo-300 hover:shadow-md'
        }`}
      >
        {isEditable && onRemove && (
          <button onClick={(e) => { e.stopPropagation(); onRemove(); }} className={`absolute top-1 right-1 p-0.5 opacity-0 group-hover:opacity-100 rounded z-10 transition-opacity ${isDarkMode ? 'hover:bg-white/20' : 'hover:bg-indigo-50'}`}>
            <X className={`h-2.5 w-2.5 ${isDarkMode ? 'text-white/70' : 'text-slate-500'}`} />
          </button>
        )}
        
        {/* Small: Icon + Date + Events count */}
        {isSmall ? (
          <div className="flex flex-col items-center justify-center h-full gap-1">
            <div className="p-2 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600">
              <Calendar className="h-4 w-4 text-white" />
            </div>
            <div className="text-center">
              <div className={`text-lg font-bold ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>{currentDate}</div>
              <div className={`text-[9px] ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>{currentMonth}</div>
            </div>
            {events.length > 0 && (
              <div className={`text-xs font-semibold ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>{events.length} events</div>
            )}
            <button 
              onClick={() => setShowModal(true)}
              className={`mt-1 text-[10px] font-medium ${isDarkMode ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-700'}`}
            >
              View
            </button>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <div className="p-1 rounded-md bg-gradient-to-br from-indigo-500 to-purple-600">
                  <Calendar className="h-3 w-3 text-white" />
                </div>
                <div>
                  <h3 className={`text-xs font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Calendar</h3>
                  <p className={`text-[9px] ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>{currentMonth}</p>
                </div>
              </div>
              <div className="text-center">
                <div className={`text-sm font-bold tabular-nums ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>{currentDate}</div>
                <div className={`text-[9px] ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>{today.toLocaleDateString('en-US', { weekday: 'short' })}</div>
              </div>
              <button 
                onClick={() => setShowModal(true)}
                className={`p-0.5 rounded transition-colors ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`}
                title="View calendar"
              >
                <Eye className={`h-3 w-3 ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`} />
              </button>
            </div>

            {/* Events List - Compact */}
            <div className="flex-1 space-y-1">
              {(isLarge ? events : events.slice(0, 2)).map((event, idx) => (
                <div
                  key={idx}
                  className={`flex items-center gap-1.5 p-1.5 rounded border-l-2 cursor-pointer transition-colors ${
                    event.color === 'blue' 
                      ? isDarkMode ? 'bg-blue-500/20 border-blue-400 hover:bg-blue-500/30' : 'bg-blue-50 border-blue-400 hover:bg-blue-100'
                      : event.color === 'purple' 
                        ? isDarkMode ? 'bg-purple-500/20 border-purple-400 hover:bg-purple-500/30' : 'bg-purple-50 border-purple-400 hover:bg-purple-100'
                        : isDarkMode ? 'bg-amber-500/20 border-amber-400 hover:bg-amber-500/30' : 'bg-amber-50 border-amber-400 hover:bg-amber-100'
                  }`}
                >
                  <div className={`w-1 h-1 rounded-full flex-shrink-0 ${event.color === 'blue' ? 'bg-blue-500' : event.color === 'purple' ? 'bg-purple-500' : 'bg-amber-500'}`} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-[10px] font-semibold truncate leading-tight ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{event.title}</p>
                    <p className={`text-[9px] ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>{event.time}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Calendar Grid - Only show in large size */}
            {isLarge && (
              <div className={`mt-2 pt-2 ${isDarkMode ? 'border-t border-white/20' : 'border-t border-slate-200'}`}>
                <div className="grid grid-cols-7 gap-0.5 mb-1">
                  {daysOfWeek.map((day, i) => (
                    <div key={i} className={`text-center text-[9px] font-semibold py-0.5 ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>{day}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-0.5">
                  {calendarDays.slice(0, 14).map(({ day, hasEvent, isToday }) => (
                    <div
                      key={day}
                      className={`aspect-square rounded flex items-center justify-center text-[9px] font-medium cursor-pointer transition-colors ${
                        isToday 
                          ? 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white' 
                          : hasEvent 
                            ? isDarkMode ? 'bg-indigo-500/30 text-indigo-200 hover:bg-indigo-500/40' : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200' 
                            : isDarkMode ? 'text-white/60 hover:bg-white/10' : 'text-slate-600 hover:bg-cyan-50'
                      }`}
                    >
                      {day}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className={`rounded-xl max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-2xl ${isDarkMode ? 'bg-[#1a1a1a] border border-white/20' : 'bg-white'}`}
            >
              <div className={`p-4 border-b flex items-center justify-between ${isDarkMode ? 'border-white/20' : 'border-slate-200'}`}>
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600">
                    <Calendar className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Calendar</h3>
                    <p className={`text-xs ${isDarkMode ? 'text-white/60' : 'text-slate-600'}`}>{currentMonthFull}</p>
                  </div>
                </div>
                <button onClick={() => setShowModal(false)} className={`p-1 rounded ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`}>
                  <X className={`h-5 w-5 ${isDarkMode ? 'text-white/70' : 'text-slate-500'}`} />
                </button>
              </div>
              <div className="p-4 overflow-y-auto max-h-[60vh]">
                <div className="grid grid-cols-7 gap-1 mb-3">
                  {daysOfWeek.map((day, i) => (
                    <div key={i} className={`text-center text-xs font-semibold py-1 ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>{day}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-1 mb-4">
                  {calendarDays.map(({ day, hasEvent, isToday }) => (
                    <div
                      key={day}
                      className={`aspect-square rounded-lg flex items-center justify-center text-xs font-medium cursor-pointer transition-colors ${
                        isToday 
                          ? 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-md' 
                          : hasEvent 
                            ? isDarkMode ? 'bg-indigo-500/30 text-indigo-200 hover:bg-indigo-500/40 border border-indigo-400/50' : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200 border border-indigo-300' 
                            : isDarkMode ? 'text-white/60 hover:bg-white/10' : 'text-slate-600 hover:bg-cyan-50'
                      }`}
                    >
                      {day}
                    </div>
                  ))}
                </div>
                <div className="space-y-2">
                  <h4 className={`text-sm font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Upcoming Events</h4>
                  {events.map((event, idx) => (
                    <div
                      key={idx}
                      className={`p-3 rounded-lg border-l-2 ${
                        event.color === 'blue' 
                          ? isDarkMode ? 'bg-blue-500/20 border-blue-500' : 'bg-blue-50 border-blue-500'
                          : event.color === 'purple' 
                            ? isDarkMode ? 'bg-purple-500/20 border-purple-500' : 'bg-purple-50 border-purple-500'
                            : isDarkMode ? 'bg-amber-500/20 border-amber-500' : 'bg-amber-50 border-amber-500'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{event.title}</p>
                          <p className={`text-xs mt-1 ${isDarkMode ? 'text-white/60' : 'text-slate-600'}`}>{event.time}</p>
                          <p className={`text-xs mt-1 ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>{event.location} • {event.attendees} attendees</p>
                        </div>
                        <div className={`text-xs font-semibold ${isDarkMode ? 'text-white/60' : 'text-slate-600'}`}>{event.date}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Quick Actions Widget - Fully customizable with all module actions
const QuickActionsWidget = ({ onRemove, isEditable, index }) => {
  const { isDarkMode } = useTheme();
  const [isCustomizing, setIsCustomizing] = useState(false);
  
  // Load selected actions from localStorage
  const [selectedActions, setSelectedActions] = useState(() => {
    const saved = localStorage.getItem('quickActionsSelection');
    return saved ? JSON.parse(saved) : [
      'create-po', 'start-audit', 'maintenance-request', 'it-support', 'stock-count', 'approve-po'
    ];
  });

  // Save to localStorage when selection changes
  useEffect(() => {
    localStorage.setItem('quickActionsSelection', JSON.stringify(selectedActions));
  }, [selectedActions]);

  // All available actions from all modules
  const availableActions = [
    // Procurement
    { id: 'create-po', label: 'Create PO', icon: Receipt, path: '/procurement/po/new', color: 'from-cyan-500 to-blue-500', module: 'Procurement' },
    { id: 'approve-po', label: 'Approve POs', icon: CheckCircle2, path: '/procurement/approve', color: 'from-emerald-500 to-green-500', module: 'Procurement' },
    { id: 'view-pos', label: 'View POs', icon: ShoppingCart, path: '/procurement', color: 'from-pink-500 to-rose-500', module: 'Procurement' },
    
    // Audit
    { id: 'start-audit', label: 'Start Audit', icon: ClipboardCheck, path: '/audit/new', color: 'from-violet-500 to-purple-500', module: 'Audit' },
    { id: 'view-audits', label: 'View Audits', icon: FileCheck, path: '/audit', color: 'from-purple-500 to-pink-500', module: 'Audit' },
    
    // Maintenance
    { id: 'maintenance-request', label: 'New Maintenance', icon: Wrench, path: '/maintenance/new', color: 'from-rose-500 to-pink-500', module: 'Maintenance' },
    { id: 'view-maintenance', label: 'View Maintenance', icon: Wrench, path: '/maintenance', color: 'from-red-500 to-rose-500', module: 'Maintenance' },
    
    // IT Support
    { id: 'it-support', label: 'IT Support Ticket', icon: Monitor, path: '/it-support/new', color: 'from-indigo-500 to-blue-500', module: 'IT Support' },
    { id: 'view-it-tickets', label: 'IT Tickets', icon: Bug, path: '/it-support', color: 'from-blue-500 to-indigo-500', module: 'IT Support' },
    
    // Inventory
    { id: 'stock-count', label: 'Stock Count', icon: Box, path: '/inventory/count/new', color: 'from-amber-500 to-orange-500', module: 'Inventory' },
    { id: 'view-inventory', label: 'View Inventory', icon: Box, path: '/inventory', color: 'from-sky-500 to-blue-500', module: 'Inventory' },
    
    // Batch Control
    { id: 'new-batch', label: 'New Batch', icon: ScanLine, path: '/batch-control/new', color: 'from-purple-500 to-pink-500', module: 'Batch Control' },
    { id: 'scan-batch', label: 'Scan Batch', icon: ScanLine, path: '/batch-control/scan', color: 'from-cyan-500 to-blue-500', module: 'Batch Control' },
    
    // Assets
    { id: 'asset-request', label: 'Asset Request', icon: Package, path: '/assets/request', color: 'from-blue-500 to-cyan-500', module: 'Assets' },
    { id: 'view-assets', label: 'View Assets', icon: Package, path: '/assets', color: 'from-yellow-600 to-amber-600', module: 'Assets' },
    
    // Projects
    { id: 'new-project', label: 'New Project', icon: Briefcase, path: '/projects/new', color: 'from-stone-500 to-stone-700', module: 'Projects' },
    { id: 'view-projects', label: 'View Projects', icon: Briefcase, path: '/projects', color: 'from-slate-600 to-slate-800', module: 'Projects' },
    
    // HR
    { id: 'new-employee', label: 'New Employee', icon: Users, path: '/hr/new', color: 'from-blue-500 to-indigo-500', module: 'HR' },
    { id: 'view-hr', label: 'View HR', icon: Users, path: '/hr', color: 'from-indigo-500 to-blue-500', module: 'HR' },
    
    // Tracker
    { id: 'new-list', label: 'New List', icon: ClipboardList, path: '/tracker/new', color: 'from-purple-500 to-purple-600', module: 'Tracker' },
    { id: 'view-tracker', label: 'View Tracker', icon: ClipboardList, path: '/tracker', color: 'from-violet-500 to-purple-500', module: 'Tracker' },
  ];

  const actions = availableActions.filter(a => selectedActions.includes(a.id));

  const toggleAction = (actionId) => {
    setSelectedActions(prev => 
      prev.includes(actionId) 
        ? prev.filter(id => id !== actionId)
        : [...prev, actionId]
    );
  };

  return (
    <>
      <div className="relative">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-gradient-to-br from-slate-600 to-slate-700">
              <Zap className="h-3.5 w-3.5 text-white" />
            </div>
            <h3 className={`text-base font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Quick Actions</h3>
          </div>
          <button
            onClick={() => setIsCustomizing(true)}
            className={`p-1.5 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`}
            title="Customize actions"
          >
            <Settings className={`h-3.5 w-3.5 ${isDarkMode ? 'text-white/70' : 'text-slate-600'}`} />
          </button>
        </div>

        {/* Grid layout matching stats style */}
        <div className="grid grid-cols-2 gap-2.5 auto-rows-fr">
          {actions.length > 0 ? (
            actions.map((action) => (
              <Link
                key={action.id}
                to={action.path}
                className={`group/action relative rounded-xl p-3.5 border transition-all duration-200 ease-out h-full flex flex-col hover:scale-[1.02] ${
                  isDarkMode 
                    ? 'bg-white/10 backdrop-blur-md border-white/20 hover:border-white/35 hover:bg-white/15' 
                    : 'bg-gradient-to-br from-white to-slate-50 border-slate-200/60 hover:border-cyan-400/60 hover:shadow-md hover:shadow-cyan-100/30'
                }`}
                title={action.label}
              >
                <div className="flex flex-col gap-2 flex-1 min-h-0">
                  <div className={`p-2 rounded-lg bg-gradient-to-br ${action.color} shadow-sm flex-shrink-0 w-fit`}>
                    <action.icon className="h-3.5 w-3.5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0 flex flex-col">
                    <p className={`text-[10px] font-medium uppercase tracking-wider truncate mb-1 ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>{action.module}</p>
                    <span className={`text-base font-bold truncate block leading-tight ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{action.label}</span>
                  </div>
                </div>
              </Link>
            ))
          ) : (
            <button
              onClick={() => setIsCustomizing(true)}
              className={`col-span-2 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-dashed transition-colors ${
                isDarkMode ? 'border-white/30 text-white/60 hover:border-cyan-400/60 hover:text-cyan-400' : 'border-slate-300 text-slate-500 hover:border-cyan-400 hover:text-cyan-600'
              }`}
            >
              <Plus className="h-4 w-4" />
              <span className="text-sm font-medium">Add actions</span>
            </button>
          )}
        </div>
      </div>

      {/* Customization Modal */}
      <AnimatePresence>
        {isCustomizing && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setIsCustomizing(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            >
              {/* Header */}
              <div className="px-6 py-5 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold text-slate-900">Customize Quick Actions</h3>
                  <p className="text-sm text-slate-600 mt-1">Select actions to display ({selectedActions.length} selected)</p>
                </div>
                <button
                  onClick={() => setIsCustomizing(false)}
                  className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <X className="h-5 w-5 text-slate-600" />
                </button>
              </div>

              {/* Actions Grid */}
              <div className="flex-1 overflow-y-auto p-6">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {availableActions.map((action) => {
                    const isSelected = selectedActions.includes(action.id);
                    return (
                      <button
                        key={action.id}
                        onClick={() => toggleAction(action.id)}
                        className={`p-4 rounded-xl border-2 transition-all text-left ${
                          isSelected
                            ? 'border-cyan-500 bg-cyan-50'
                            : 'border-slate-200 bg-white hover:border-slate-300'
                        }`}
                      >
                        <div className="flex items-start gap-3 mb-2">
                          <div className={`p-2 rounded-lg bg-gradient-to-br ${action.color} flex-shrink-0`}>
                            <action.icon className="h-4 w-4 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-slate-900">{action.label}</p>
                            <p className="text-xs text-slate-500 mt-0.5">{action.module}</p>
                          </div>
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                            isSelected
                              ? 'border-cyan-500 bg-cyan-500'
                              : 'border-slate-300'
                          }`}>
                            {isSelected && <CheckCircle className="h-3 w-3 text-white" />}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-3">
                <button
                  onClick={() => setIsCustomizing(false)}
                  className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => setIsCustomizing(false)}
                  className="px-4 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-500 text-white hover:shadow-lg transition-all"
                >
                  Done
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// My Requests Widget - Show tickets from all modules
const MyRequestsWidget = ({ onRemove, isEditable, index, size = 'medium' }) => {
  const { isDarkMode } = useTheme();
  const [showModal, setShowModal] = useState(false);
  const requests = [
    { id: 1, module: 'Asset', title: 'Laptop Replacement Request', status: 'pending', date: '2 days ago', priority: 'high', ticketId: 'AST-2024-001', description: 'Need replacement for damaged laptop screen' },
    { id: 2, module: 'Maintenance', title: 'AC Unit Repair - Office 3B', status: 'in-progress', date: '1 day ago', priority: 'medium', ticketId: 'MNT-2024-045', description: 'AC not cooling properly, needs immediate attention' },
    { id: 3, module: 'IT Support', title: 'Email Access Issue', status: 'resolved', date: '3 days ago', priority: 'low', ticketId: 'IT-2024-089', description: 'Unable to access email account' },
    { id: 4, module: 'Procurement', title: 'Office Supplies Purchase', status: 'pending', date: '5 days ago', priority: 'medium', ticketId: 'PROC-2024-123', description: 'Request for stationery and office supplies' },
    { id: 5, module: 'Asset', title: 'Monitor Upgrade Request', status: 'pending', date: '1 week ago', priority: 'low', ticketId: 'AST-2024-002', description: 'Request for larger monitor for design work' },
  ];

  const getStatusColor = (status) => {
    switch(status) {
      case 'pending': return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'in-progress': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'resolved': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      default: return 'bg-slate-50 text-slate-700 border-slate-200';
    }
  };

  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'high': return 'bg-red-500';
      case 'medium': return 'bg-amber-500';
      case 'low': return 'bg-slate-400';
      default: return 'bg-slate-400';
    }
  };

  const isSmall = size === 'small';
  const isLarge = size === 'large';
  const pendingCount = requests.filter(r => r.status === 'pending').length;
  const highPriorityCount = requests.filter(r => r.priority === 'high').length;

  return (
    <>
      <div className={`relative group rounded-lg border transition-all h-full flex flex-col ${isSmall ? 'p-2' : isLarge ? 'p-4' : 'p-3'} ${
        isDarkMode ? 'bg-white/10 backdrop-blur-xl border-white/20 hover:border-white/30' : 'bg-white border-slate-200 hover:border-indigo-300 hover:shadow-md'
      }`}>
        {isEditable && onRemove && (
          <button onClick={(e) => { e.stopPropagation(); onRemove(); }} className={`absolute top-1 right-1 p-0.5 opacity-0 group-hover:opacity-100 rounded z-10 transition-opacity ${isDarkMode ? 'hover:bg-white/20' : 'hover:bg-slate-50'}`}>
            <X className={`h-2.5 w-2.5 ${isDarkMode ? 'text-white/70' : 'text-slate-500'}`} />
          </button>
        )}
        
        {/* Small: Icon + Count only */}
        {isSmall ? (
          <div className="flex flex-col items-center justify-center h-full gap-1">
            <div className="p-2 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600">
              <ClipboardList className="h-4 w-4 text-white" />
            </div>
            <div className="text-center">
              <div className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{requests.length}</div>
              {pendingCount > 0 && (
                <div className="text-xs font-semibold text-amber-500">{pendingCount} pending</div>
              )}
            </div>
            <button 
              onClick={() => setShowModal(true)}
              className={`mt-1 text-[10px] font-medium ${isDarkMode ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-700'}`}
            >
              View
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <div className="p-1 rounded-md bg-gradient-to-br from-indigo-500 to-purple-600">
                  <ClipboardList className="h-3 w-3 text-white" />
                </div>
                <div>
                  <h3 className={`text-xs font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Requests</h3>
                  <p className={`text-[9px] ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>{pendingCount} pending</p>
                </div>
                <span className={`text-[10px] font-semibold text-indigo-500 px-1 py-0.5 rounded ${isDarkMode ? 'bg-white/10' : 'bg-indigo-50'}`}>{requests.length}</span>
              </div>
              <button 
                onClick={() => setShowModal(true)}
                className={`p-0.5 rounded transition-colors ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`}
                title="View all requests"
              >
                <Eye className={`h-3 w-3 ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`} />
              </button>
            </div>

            <div className="flex-1 space-y-1">
              {(isLarge ? requests : requests.slice(0, 2)).map((request) => (
                <div
                  key={request.id}
                  className={`rounded border transition-colors cursor-pointer p-1.5 ${isDarkMode ? 'bg-white/5 border-white/10 hover:bg-white/10' : 'bg-slate-50 border-slate-200 hover:bg-slate-100'}`}
                >
                  <div className="flex items-start justify-between gap-1.5">
                    <div className="flex items-center gap-1 flex-1 min-w-0">
                      <div className={`w-1 h-1 rounded-full flex-shrink-0 ${getPriorityColor(request.priority)} mt-0.5`} />
                      <div className="flex-1 min-w-0">
                        <p className={`text-[10px] font-semibold truncate leading-tight ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{request.title}</p>
                        <p className={`text-[9px] ${isDarkMode ? 'text-white/50' : 'text-slate-600'}`}>{request.module} • {request.date}</p>
                      </div>
                    </div>
                    <span className={`rounded font-medium border whitespace-nowrap flex-shrink-0 ${getStatusColor(request.status)} px-1.5 py-0.5 text-[9px]`}>
                      {request.status.replace('-', ' ')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className={`rounded-xl max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-2xl ${isDarkMode ? 'bg-[#1a1a1a] border border-white/20' : 'bg-white'}`}
            >
              <div className={`p-4 border-b flex items-center justify-between ${isDarkMode ? 'border-white/20' : 'border-slate-200'}`}>
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600">
                    <ClipboardList className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>My Requests</h3>
                    <p className={`text-xs ${isDarkMode ? 'text-white/60' : 'text-slate-600'}`}>{requests.length} tickets across modules</p>
                  </div>
                </div>
                <button onClick={() => setShowModal(false)} className={`p-1 rounded ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`}>
                  <X className={`h-5 w-5 ${isDarkMode ? 'text-white/70' : 'text-slate-500'}`} />
                </button>
              </div>
              <div className="p-4 overflow-y-auto max-h-[60vh] space-y-2">
                {requests.map((request) => (
                  <div key={request.id} className={`p-3 rounded-lg border ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-2 flex-1">
                        <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${getPriorityColor(request.priority)}`} />
                        <div className="flex-1">
                          <p className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{request.title}</p>
                          <p className={`text-xs mt-1 ${isDarkMode ? 'text-white/60' : 'text-slate-600'}`}>{request.description}</p>
                          <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                            <span>{request.module}</span>
                            <span>•</span>
                            <span>Ticket: {request.ticketId}</span>
                            <span>•</span>
                            <span>{request.date}</span>
                          </div>
                        </div>
                      </div>
                      <span className={`px-2 py-1 rounded-md text-xs font-medium border ${getStatusColor(request.status)} whitespace-nowrap`}>
                        {request.status.replace('-', ' ')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Module Icon – Clean, minimal design
const ModuleIcon = ({ module, delay = 0 }) => {
  const { isDarkMode } = useTheme();
  return (
  <Link to={module.path || '#'} className="block">
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay, duration: 0.25 }}
      className={`group flex flex-col items-center gap-3 p-5 rounded-xl border transition-colors cursor-pointer ${
        isDarkMode 
          ? 'bg-white/10 backdrop-blur-xl border-white/20 hover:border-white/30 hover:bg-white/[0.12]' 
          : 'bg-white border-slate-200/80 hover:border-slate-300 hover:bg-slate-50/50'
      }`}
    >
      <div className={`p-3 rounded-xl bg-gradient-to-br ${module.gradient}`}>
        <module.icon className="h-7 w-7 text-white" />
      </div>
      <div className="text-center">
        <h3 className={`text-sm font-semibold mb-0.5 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>{module.name}</h3>
        <p className={`text-xs leading-snug line-clamp-2 ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>{module.description}</p>
      </div>
    </motion.div>
  </Link>
  );
};

// Main Dashboard Component
// Sortable Widget Wrapper Component
const SortableWidget = ({ id, widget, children, onResize, isEditing }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const sizeOptions = ['small', 'medium', 'large'];
  const currentSizeIndex = sizeOptions.indexOf(widget.size || 'medium');
  const nextSize = sizeOptions[(currentSizeIndex + 1) % sizeOptions.length];
  const prevSize = sizeOptions[(currentSizeIndex - 1 + sizeOptions.length) % sizeOptions.length];

  const getSizeClasses = (size) => {
    switch(size) {
      case 'small': return 'md:col-span-4';
      case 'medium': return 'md:col-span-4';
      case 'large': return 'md:col-span-12';
      default: return 'md:col-span-4';
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative group ${getSizeClasses(widget.size || 'medium')}`}
    >
      {isEditing && (
        <>
          {/* Drag Handle */}
          <div
            {...attributes}
            {...listeners}
            className="absolute -top-2 -left-2 z-20 p-1.5 bg-slate-700 text-white rounded-lg cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
            title="Drag to rearrange"
          >
            <GripVertical className="h-4 w-4" />
          </div>
          
          {/* Resize Controls */}
          <div className="absolute -top-2 -right-2 z-20 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onResize(id, prevSize);
              }}
              className="p-1.5 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors shadow-lg"
              title="Make smaller"
            >
              <Minimize2 className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onResize(id, nextSize);
              }}
              className="p-1.5 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors shadow-lg"
              title="Make larger"
            >
              <Maximize2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </>
      )}
      {children}
    </div>
  );
};

const MainDashboard = ({ searchQuery = '' }) => {
  const { isDarkMode } = useTheme();
  const [currentTime, setCurrentTime] = useState(() => new Date());
  const [isEditing, setIsEditing] = useState(false);
  const [activeId, setActiveId] = useState(null);
  
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    const t = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const getGreeting = () => {
    const h = currentTime.getHours();
    if (h < 12) return 'Good morning';
    if (h < 18) return 'Good afternoon';
    return 'Good evening';
  };
  const WIDGET_VERSION = '2.3'; // Increment when widget structure changes
  const [widgets, setWidgets] = useState(() => {
    const saved = localStorage.getItem('dashboardWidgets');
    const savedVersion = localStorage.getItem('dashboardWidgetsVersion');
    const defaultWidgets = [
      { id: 'requests', type: 'stats', enabled: true, size: 'medium', order: 0 },
      { id: 'tasks', type: 'stats', enabled: true, size: 'medium', order: 1 },
      { id: 'messages', type: 'stats', enabled: true, size: 'medium', order: 2 },
      { id: 'completed', type: 'stats', enabled: true, size: 'medium', order: 3 },
      { id: 'approvals', type: 'stats', enabled: true, size: 'medium', order: 4 },
      { id: 'overdue', type: 'stats', enabled: true, size: 'medium', order: 5 },
      { id: 'weather', type: 'weather', enabled: true, size: 'small', order: 6 },
      { id: 'tasksList', type: 'tasks', enabled: true, size: 'medium', order: 7 },
      { id: 'announcements', type: 'announcements', enabled: true, size: 'large', order: 8 },
      { id: 'stories', type: 'stories', enabled: true, size: 'large', order: 9 },
      { id: 'notes', type: 'notes', enabled: true, size: 'medium', order: 10 },
      { id: 'calendar', type: 'calendar', enabled: true, size: 'medium', order: 11 },
      { id: 'quickActions', type: 'quickActions', enabled: true, size: 'medium', order: 12 },
      { id: 'myRequests', type: 'myRequests', enabled: true, size: 'medium', order: 13 },
    ];
    
    // If version doesn't match, reset to defaults
    if (savedVersion !== WIDGET_VERSION) {
      localStorage.setItem('dashboardWidgetsVersion', WIDGET_VERSION);
      return defaultWidgets;
    }
    
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        
        // Migration: Map old widget IDs to new ones
        const idMapping = {
          'revenue': 'requests',
          'projects': 'tasks',
          'employees': 'messages',
          'completion': 'completed'
        };
        
        // Check if we have old widget IDs that need migration
        const hasOldWidgets = parsed.some(w => idMapping[w.id]);
        
        if (hasOldWidgets) {
          // Migrate old widgets to new ones
          const migrated = parsed.map(w => {
            if (idMapping[w.id]) {
              return { ...w, id: idMapping[w.id] };
            }
            return w;
          });
          
          // Remove duplicates and ensure we have all new default widgets
          const existingIds = new Set(migrated.map(w => w.id));
          defaultWidgets.forEach(defaultWidget => {
            if (!existingIds.has(defaultWidget.id)) {
              migrated.push(defaultWidget);
            }
          });
          
          // Ensure announcements and stories are always enabled
          const announcementsWidget = migrated.find(w => w.id === 'announcements');
          const storiesWidget = migrated.find(w => w.id === 'stories');
          if (announcementsWidget) announcementsWidget.enabled = true;
          if (storiesWidget) storiesWidget.enabled = true;
          
          return migrated;
        }
        
        // Ensure new widgets are added if they don't exist
        const existingIds = new Set(parsed.map(w => w.id));
        const newWidgets = defaultWidgets.filter(w => !existingIds.has(w.id));
        if (newWidgets.length > 0) {
          parsed.push(...newWidgets);
        }
        
        // Check if we have any stats widgets at all
        const hasStatsWidgets = parsed.some(w => w.type === 'stats');
        if (!hasStatsWidgets) {
          // Add default stats widgets if none exist
          const statsWidgets = defaultWidgets.filter(w => w.type === 'stats');
          parsed.push(...statsWidgets);
        }
        
        // Ensure announcements and stories are always enabled
        const announcementsWidget = parsed.find(w => w.id === 'announcements');
        const storiesWidget = parsed.find(w => w.id === 'stories');
        if (announcementsWidget) announcementsWidget.enabled = true;
        if (storiesWidget) storiesWidget.enabled = true;
        
        // Ensure all widgets have size and order
        parsed.forEach((widget, index) => {
          if (!widget.size) widget.size = 'medium';
          if (widget.order === undefined) widget.order = index;
        });
        
        // Sort by order
        parsed.sort((a, b) => (a.order || 0) - (b.order || 0));
        
        return parsed;
      } catch (e) {
        // If parsing fails, return defaults
        return defaultWidgets;
      }
    }
    return defaultWidgets;
  });

  useEffect(() => {
    localStorage.setItem('dashboardWidgets', JSON.stringify(widgets));
    localStorage.setItem('dashboardWidgetsVersion', WIDGET_VERSION);
  }, [widgets]);

  // Ensure new widgets are always present and have size/order
  useEffect(() => {
    const updated = [...widgets];
    let hasChanges = false;
    
    // Add missing widgets
    const hasQuickActions = updated.some(w => w.id === 'quickActions');
    const hasMyRequests = updated.some(w => w.id === 'myRequests');
    
    if (!hasQuickActions) {
      updated.push({ id: 'quickActions', type: 'quickActions', enabled: true, size: 'medium', order: updated.length });
      hasChanges = true;
    }
    if (!hasMyRequests) {
      updated.push({ id: 'myRequests', type: 'myRequests', enabled: true, size: 'medium', order: updated.length });
      hasChanges = true;
    }
    
    // Ensure all widgets have size and order
    updated.forEach((widget, index) => {
      if (!widget.size) {
        widget.size = 'medium';
        hasChanges = true;
      }
      if (widget.order === undefined) {
        widget.order = index;
        hasChanges = true;
      }
    });
    
    if (hasChanges) {
      setWidgets(updated);
    }
  }, [widgets]);

  // Handle drag end
  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      setWidgets((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        
        const reordered = arrayMove(items, oldIndex, newIndex);
        // Update order property
        return reordered.map((item, index) => ({
          ...item,
          order: index
        }));
      });
    }
    setActiveId(null);
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  // Handle resize
  const handleResize = (widgetId, newSize) => {
    setWidgets((items) =>
      items.map((item) =>
        item.id === widgetId ? { ...item, size: newSize } : item
      )
    );
  };

  // User-tailored stats data
  const requestsData = Array.from({ length: 7 }, (_, i) => ({
    name: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i],
    value: Math.floor(Math.random() * 20) + 5
  }));

  const tasksData = Array.from({ length: 7 }, (_, i) => ({
    name: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i],
    value: Math.floor(Math.random() * 15) + 3
  }));

  const messagesData = Array.from({ length: 7 }, (_, i) => ({
    name: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i],
    value: Math.floor(Math.random() * 30) + 10
  }));

  const approvalsData = Array.from({ length: 7 }, (_, i) => ({
    name: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i],
    value: Math.floor(Math.random() * 10) + 2
  }));

  const overdueData = Array.from({ length: 7 }, (_, i) => ({
    name: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i],
    value: Math.floor(Math.random() * 5) + 1
  }));

  const completedData = Array.from({ length: 7 }, (_, i) => ({
    name: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i],
    value: Math.floor(Math.random() * 12) + 5
  }));

  const modules = [
    { name: 'Projects', description: 'Track construction projects', icon: Briefcase, gradient: 'from-stone-500 to-stone-700', path: '/projects' },
    { name: 'Batch Control', description: 'Manage production batches', icon: ScanLine, gradient: 'from-cyan-500 to-blue-500', path: '/batch-control' },
    { name: 'Procurement', description: 'Manage vendors', icon: ShoppingCart, gradient: 'from-pink-500 to-rose-500', path: '/procurement' },
    { name: 'COGS', description: 'Monitor costs', icon: DollarSign, gradient: 'from-emerald-500 to-green-500', path: '/cogs' },
    { name: 'Scorecard', description: 'Track performance', icon: BarChart2, gradient: 'from-indigo-500 to-purple-500', path: '/scorecard' },
    { name: 'Audit', description: 'Compliance tracking', icon: ClipboardCheck, gradient: 'from-violet-500 to-purple-500', path: '/audit' },
    { name: 'Archive', description: 'Document repository', icon: Archive, gradient: 'from-amber-500 to-orange-500', path: '/archive' },
    { name: 'HR', description: 'Employee management', icon: Users, gradient: 'from-blue-500 to-indigo-500', path: '/hr' },
    { name: 'Assets', description: 'Company assets', icon: Package, gradient: 'from-yellow-600 to-amber-600', path: '/assets' },
    { name: 'Inventory', description: 'Stock management', icon: Box, gradient: 'from-sky-500 to-blue-500', path: '/inventory' },
    { name: 'Maintenance', description: 'Maintenance tasks', icon: Wrench, gradient: 'from-rose-500 to-pink-500', path: '/maintenance' },
    { name: 'IT Support', description: 'Technical support', icon: Monitor, gradient: 'from-indigo-500 to-blue-500', path: '/it-support' },
    { name: 'SOP', description: 'Standard procedures', icon: BookOpen, gradient: 'from-emerald-500 to-teal-500', path: '/sop' },
    { name: 'Pulse', description: 'Performance monitoring', icon: Activity, gradient: 'from-rose-500 to-pink-500', path: '/pulse' },
    { name: 'Supply Chain', description: 'Supply operations', icon: Package, gradient: 'from-green-500 to-emerald-500', path: '/supply-chain' },
    { name: 'SCMS', description: 'Supply chain system', icon: ShoppingCart, gradient: 'from-cyan-500 to-blue-500', path: '/scms' },
    { name: 'Warehouse', description: 'Warehouse management', icon: Warehouse, gradient: 'from-orange-500 to-amber-600', path: '/warehouse' },
    { name: 'Logistics', description: 'Logistics & fleet management', icon: Truck, gradient: 'from-blue-500 to-indigo-600', path: '/logistics' },
    { name: 'Res', description: 'Real estate management', icon: Home, gradient: 'from-amber-600 to-orange-600', path: '/res' },
    { name: 'Bloom', description: 'Learning system', icon: GraduationCap, gradient: 'from-purple-500 to-pink-500', path: '/bloom' },
    { name: 'Creatives', description: 'Content management', icon: Palette, gradient: 'from-indigo-500 to-purple-500', path: '/creatives' },
    { name: 'Chains', description: 'Supply logistics', icon: Building2, gradient: 'from-blue-500 to-indigo-500', path: '/chains' },
    { name: 'Projex', description: 'Project management', icon: Briefcase, gradient: 'from-orange-500 to-red-500', path: '/projex' },
    { name: 'Tracker', description: 'Track and collaborate on lists and projects', icon: ClipboardList, gradient: 'from-purple-500 to-purple-600', path: '/tracker' },
    { name: 'Financial', description: 'Complete accounting & financial management', icon: DollarSign, gradient: 'from-green-500 to-emerald-600', path: '/financial' },
    { name: 'Sales', description: 'B2B sales, credit, orders & supply workflows', icon: Receipt, gradient: 'from-teal-500 to-cyan-600', path: '/sales' },
  ];

  const enabledWidgets = widgets.filter(w => w.enabled);
  
  // Ensure we have at least some stats widgets enabled on first load
  useEffect(() => {
    const hasStatsWidgets = enabledWidgets.some(w => w.type === 'stats');
    if (!hasStatsWidgets && widgets.length > 0) {
      // If no stats widgets are enabled, enable the first few by default
      const statsWidgets = widgets.filter(w => w.type === 'stats');
      if (statsWidgets.length > 0) {
        setWidgets(prevWidgets => prevWidgets.map(w => 
          (w.type === 'stats' && statsWidgets.indexOf(w) < 4) ? { ...w, enabled: true } : w
        ));
      }
    }
  }, []); // Only run once on mount

  const handleRemoveWidget = (id) => {
    setWidgets(widgets.map(w => w.id === id ? { ...w, enabled: false } : w));
  };

  const renderWidget = (widget, index) => {
    switch (widget.type) {
      case 'stats':
        const statsConfig = {
          requests: { 
            title: 'Pending Requests', 
            value: '12', 
            change: '+3 new', 
            trend: 'up', 
            icon: FileText, 
            gradient: 'linear-gradient(135deg, #f97316 0%, #ec4899 100%)', 
            subtitle: 'Awaiting your action'
          },
          tasks: { 
            title: 'My Tasks', 
            value: '8', 
            change: '2 due today', 
            trend: 'up', 
            icon: ClipboardCheck, 
            gradient: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)', 
            subtitle: 'Active assignments'
          },
          messages: { 
            title: 'Unread Messages', 
            value: '24', 
            change: '+5 new', 
            trend: 'up', 
            icon: MessageSquare, 
            gradient: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)', 
            subtitle: 'Require attention'
          },
          approvals: { 
            title: 'Pending Approvals', 
            value: '7', 
            change: '3 urgent', 
            trend: 'up', 
            icon: CheckCircle2, 
            gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', 
            subtitle: 'Need your review'
          },
          overdue: { 
            title: 'Overdue Items', 
            value: '3', 
            change: 'Action needed', 
            trend: 'down', 
            icon: AlertTriangle, 
            gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', 
            subtitle: 'Past due date'
          },
          completed: { 
            title: 'Completed This Week', 
            value: '18', 
            change: '+4 tasks', 
            trend: 'up', 
            icon: CheckCircle, 
            gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', 
            subtitle: 'Great progress!'
          },
        };
        const config = statsConfig[widget.id];
        if (!config) return null;
        return (
          <GradientStatCard
            key={widget.id}
            title={config.title}
            value={config.value}
            change={config.change}
            trend={config.trend}
            gradient={config.gradient}
            icon={config.icon}
            subtitle={config.subtitle}
            onRemove={() => handleRemoveWidget(widget.id)}
            isEditable={isEditing}
            index={index}
          />
        );
      case 'weather':
        return <WeatherWidget key={widget.id} onRemove={() => handleRemoveWidget(widget.id)} isEditable={isEditing} index={index} size={widget.size || 'medium'} />;
      case 'tasks':
        return <TasksWidget key={widget.id} onRemove={() => handleRemoveWidget(widget.id)} isEditable={isEditing} index={index} size={widget.size || 'medium'} />;
      case 'announcements':
        return <AnnouncementsWidget key={widget.id} onRemove={() => handleRemoveWidget(widget.id)} isEditable={isEditing} index={index} size={widget.size || 'medium'} />;
      case 'stories':
        return <StoriesWidget key={widget.id} onRemove={() => handleRemoveWidget(widget.id)} isEditable={isEditing} index={index} size={widget.size || 'medium'} />;
      case 'notes':
        return <StickyNotesWidget key={widget.id} onRemove={() => handleRemoveWidget(widget.id)} isEditable={isEditing} index={index} size={widget.size || 'medium'} />;
      case 'calendar':
        return <CalendarWidget key={widget.id} onRemove={() => handleRemoveWidget(widget.id)} isEditable={isEditing} index={index} size={widget.size || 'medium'} />;
      case 'quickActions':
        return <QuickActionsWidget key={widget.id} onRemove={() => handleRemoveWidget(widget.id)} isEditable={isEditing} index={index} size={widget.size || 'medium'} />;
      case 'myRequests':
        return <MyRequestsWidget key={widget.id} onRemove={() => handleRemoveWidget(widget.id)} isEditable={isEditing} index={index} size={widget.size || 'medium'} />;
      default:
        return null;
    }
  };

  const filteredModules = modules.filter(module =>
    module.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    module.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Weekly activity data for hero chart (Recharts)
  const weeklyActivityData = [
    { day: 'Mon', activity: 42, fill: 'url(#heroGradient)' },
    { day: 'Tue', activity: 58, fill: 'url(#heroGradient)' },
    { day: 'Wed', activity: 35, fill: 'url(#heroGradient)' },
    { day: 'Thu', activity: 71, fill: 'url(#heroGradient)' },
    { day: 'Fri', activity: 64, fill: 'url(#heroGradient)' },
    { day: 'Sat', activity: 28, fill: 'url(#heroGradient)' },
    { day: 'Sun', activity: 52, fill: 'url(#heroGradient)' },
  ];

  return (
    <div className={`min-h-screen w-full overflow-hidden font-sans transition-colors duration-300 ${
      isDarkMode 
        ? 'bg-[#050505] text-white selection:bg-indigo-500' 
        : 'bg-gradient-to-br from-cyan-50 via-blue-50 via-indigo-50 to-violet-50 text-slate-900 selection:bg-indigo-200'
    }`}>
      {/* HUD Backdrop Background - only on dashboard, behind all content */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        {isDarkMode ? (
          <>
            <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-indigo-600/20 blur-[120px] rounded-full" />
            <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-rose-600/10 blur-[120px] rounded-full" />
            <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 brightness-100" />
          </>
        ) : (
          <>
            <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-cyan-200/30 blur-[120px] rounded-full" />
            <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-indigo-200/20 blur-[120px] rounded-full" />
          </>
        )}
      </div>

      {/* Dashboard content - above backdrop */}
      <div className="relative z-10">
      {/* Top Bar - Compact Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className={`w-full sticky top-0 z-50 ${
          isDarkMode 
            ? 'bg-white/10 backdrop-blur-xl border-b border-white/20' 
            : 'bg-white/95 backdrop-blur-xl border-b-2 border-cyan-200/60 shadow-lg'
        }`}
      >
        <div className="max-w-[1920px] mx-auto px-6 lg:px-12 py-5">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            {/* Left: Greeting + Time */}
            <div className="flex items-center gap-6">
              <div>
                <p className={`text-xs font-bold uppercase tracking-widest ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>{getGreeting()}</p>
                <h1 className={`text-3xl font-extrabold ${isDarkMode ? 'text-white' : 'bg-gradient-to-r from-cyan-600 via-blue-600 to-indigo-600 bg-clip-text text-transparent'}`}>
                  Welcome back
                </h1>
              </div>
              <div className={`hidden md:block w-px h-12 ${isDarkMode ? 'bg-white/20' : 'bg-gradient-to-b from-transparent via-cyan-300 to-transparent'}`} />
              <div className="text-right">
                <p className={`text-xl font-mono font-bold tabular-nums ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
                <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-slate-600'}`}>
                  {currentTime.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                </p>
              </div>
            </div>

            {/* Right: Weather + Immersive + Customize */}
            <div className="flex items-center gap-4">
              {/* Compact Weather */}
              {enabledWidgets.some(w => w.type === 'weather') && <CompactWeatherWidget />}

              {/* Lyncs Neo Immersive */}
              <Link to="/immersive-neo">
                <motion.button
                  className={`flex items-center gap-2 px-5 py-3 rounded-2xl font-semibold shadow-xl hover:scale-105 transition-all ${
                    isDarkMode
                      ? 'bg-gradient-to-r from-indigo-600 via-violet-600 to-cyan-600 text-white hover:brightness-110'
                      : 'bg-gradient-to-r from-indigo-500 via-violet-500 to-cyan-500 text-white hover:shadow-2xl'
                  }`}
                  whileTap={{ scale: 0.97 }}
                >
                  <Maximize2 className="h-5 w-5" />
                  Lyncs Neo
                </motion.button>
              </Link>
              
              {/* Customize Button */}
              <motion.button
                onClick={() => setIsEditing(!isEditing)}
                className={`flex items-center gap-2 px-5 py-3 rounded-2xl font-semibold shadow-xl hover:scale-105 transition-all ${
                  isDarkMode 
                    ? 'bg-indigo-600 hover:bg-indigo-500 text-white' 
                    : 'bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-500 text-white hover:shadow-2xl'
                }`}
                whileTap={{ scale: 0.97 }}
              >
                {isEditing ? <CheckCircle className="h-5 w-5" /> : <Settings className="h-5 w-5" />}
                {isEditing ? 'Done' : 'Customize'}
              </motion.button>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Main Content - Completely New 3-Column Layout */}
      <div className="max-w-[1920px] mx-auto px-6 lg:px-12 py-8">
        {/* Row 1: Stories - Centered */}
        {enabledWidgets.some(w => w.type === 'stories') && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="mb-8 flex justify-center"
          >
            <div className="w-full max-w-4xl">
              {(() => {
                const w = enabledWidgets.find(w => w.type === 'stories');
                const i = enabledWidgets.findIndex(x => x.id === w?.id);
                return w ? renderWidget(w, i) : null;
              })()}
            </div>
          </motion.div>
        )}

        {/* Row 2: Stats + Quick Actions (Left Half) | Company News (Right Half) - isolate so z-index works and news cannot paint over left */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8 isolate">
          {/* Left Half: Combined Stats + Quick Actions Widget */}
          {(enabledWidgets.some(w => w.type === 'stats') || enabledWidgets.some(w => w.type === 'quickActions')) && (
            <div className={`relative z-10 rounded-2xl p-5 border-2 shadow-lg ${
              isDarkMode ? 'bg-white/10 backdrop-blur-xl border-white/20' : 'bg-white border-slate-100'
            }`}>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-stretch">
                {/* Left: Quick Stats */}
                {enabledWidgets.some(w => w.type === 'stats') && (
                  <div className="flex flex-col">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="p-1.5 rounded-lg bg-gradient-to-br from-slate-600 to-slate-700">
                        <BarChart3 className="h-3.5 w-3.5 text-white" />
                      </div>
                      <h3 className={`text-base font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Quick Stats</h3>
                    </div>
                    <div className="grid grid-cols-2 gap-2.5 auto-rows-fr">
                      {enabledWidgets
                        .filter(w => w.type === 'stats')
                        .map((widget, index) => {
                          const statsOrder = ['requests', 'tasks', 'messages', 'completed', 'approvals', 'overdue'];
                          const sorted = enabledWidgets.filter(w => w.type === 'stats').sort((a, b) => statsOrder.indexOf(a.id) - statsOrder.indexOf(b.id));
                          const widgetIndex = sorted.findIndex(w => w.id === widget.id);
                          return renderWidget(widget, widgetIndex);
                        })}
                    </div>
                  </div>
                )}

                {/* Right: Quick Actions */}
                {enabledWidgets.some(w => w.type === 'quickActions') && (
                  <div className={`pl-6 flex flex-col ${isDarkMode ? 'border-l border-white/20' : 'border-l border-slate-200'}`}>
                    {(() => {
                      const w = enabledWidgets.find(w => w.type === 'quickActions');
                      const i = enabledWidgets.findIndex(x => x.id === w?.id);
                      return w ? renderWidget(w, i) : null;
                    })()}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Right Half: Company News - contained so it cannot paint or affect left column */}
          {enabledWidgets.some(w => w.type === 'announcements') && (
            <div className="relative z-0 overflow-hidden [contain:paint]">
              {(() => {
                const w = enabledWidgets.find(w => w.type === 'announcements');
                const i = enabledWidgets.findIndex(x => x.id === w?.id);
                return w ? renderWidget(w, i) : null;
              })()}
            </div>
          )}
        </div>

        {/* Row 3: Tasks, Notes, Calendar - Draggable Single Row Layout */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={enabledWidgets
              .filter(w => ['tasks', 'notes', 'calendar', 'myRequests'].includes(w.type))
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map(w => w.id)}
          >
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-8">
              {enabledWidgets
                .filter(w => ['tasks', 'notes', 'calendar'].includes(w.type))
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map((widget) => {
                  const index = enabledWidgets.findIndex(x => x.id === widget.id);
                  return (
                    <SortableWidget
                      key={widget.id}
                      id={widget.id}
                      widget={widget}
                      onResize={handleResize}
                      isEditing={isEditing}
                    >
                      {renderWidget(widget, index)}
                    </SortableWidget>
                  );
                })}
            </div>

            {/* Row 4: My Requests - Draggable */}
            {enabledWidgets
              .filter(w => w.type === 'myRequests')
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((widget) => {
                const index = enabledWidgets.findIndex(x => x.id === widget.id);
                return (
                  <SortableWidget
                    key={widget.id}
                    id={widget.id}
                    widget={widget}
                    onResize={handleResize}
                    isEditing={isEditing}
                  >
                    <div className="mb-8">
                      {renderWidget(widget, index)}
                    </div>
                  </SortableWidget>
                );
              })}
          </SortableContext>
          
          <DragOverlay>
            {activeId ? (
              <div className="opacity-50">
                {(() => {
                  const widget = enabledWidgets.find(w => w.id === activeId);
                  const index = enabledWidgets.findIndex(x => x.id === activeId);
                  return widget ? renderWidget(widget, index) : null;
                })()}
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>

        {/* Modules Section */}
        <section>
          <div className={`rounded-2xl overflow-hidden ${
            isDarkMode 
              ? 'bg-white/10 backdrop-blur-xl border border-white/20' 
              : 'bg-white border border-slate-200'
          }`}>
            <div className={`px-6 py-4 border-b ${isDarkMode ? 'border-white/10' : 'border-slate-100'}`}>
              <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                Modules
              </h2>
              <p className={`mt-0.5 text-sm ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>Access your business tools and applications</p>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-4">
                {filteredModules.map((module, index) => (
                  <ModuleIcon key={module.name} module={module} delay={index * 0.02} />
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>
      </div>
    </div>
  );
};

export default MainDashboard;
